import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useHomepage } from "src/context/homepage/homepage.provider";
import { useParams } from "react-router-dom";
import { Row, Col, Spinner } from "react-bootstrap";
import { useHubData } from "src/hooks/useHubData";
import { useHubListLivePreview } from "src/hooks/useHubListLivePreview";
import { filterPromotionCards } from "src/utils/filterPromotionCards";
import { isPayloadSource } from "src/utils/hubSource";
import { fetchPromotions } from "src/api/payload/promotions";
import { mapPromotion } from "src/utils/mapPromotion";
import { promotionsLivePreviewRequestHandler } from "src/utils/promotionsLivePreviewHandler";

import Hero from "src/components/Hero/Hero";
import PgCard from "src/components/Card/PgCard";
import SectionalTabs from "src/components/SectionalTabs/SectionalTabs";
import TitleSection from "src/components/TitleSection/TitleSection";

const Promotions = () => {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const { sectionCategory, sectionDescription } = useHomepage();
  const {
    data: promotionsData,
    loading,
    error: fetchError,
  } = useHubData(
    "promotions",
    { fetchPayloadData: fetchPromotions, ersKeyPrefix: "promotions" },
    i18n.language
  );

  const { items: previewPromotions, isLivePreview, livePreviewId } =
    useHubListLivePreview(
      promotionsData ?? [],
      isPayloadSource("promotions"),
      i18n.language,
      {
        depth: 0,
        mapDoc: mapPromotion,
        requestHandler: promotionsLivePreviewRequestHandler,
        getItemId: (item) => item?.key,
      }
    );

  const error = fetchError ? fetchError : null;
  const visibleCards = filterPromotionCards(previewPromotions, params.category, {
    livePreviewKey: isLivePreview ? livePreviewId : undefined,
  });

  const sectionCategoryLabel = sectionCategory
    ? t(`promotions.categories.${sectionCategory}`, { defaultValue: sectionCategory })
    : null;

  return (
    <PromoPage>
      <Hero
        bgColor=""
        bgImage={"home/offers-header.jpg"}
        buttonLink=""
        buttonText=""
        description={t("promotions.hero.promotionsDescription")}
        descriptionBgColor=""
        image=""
        imageAlt=""
        mainHeroText={t("promotions.hero.playgroundPromotions")}
        title=""
      >
        <SectionalTabs
          routeData={{
            route: t("routes.promotions"),
            routeCategory: "promotions",
            sectionTitle: t("promotions.hero.liveAction"),
            selectCategory: t("promotions.hero.selectPromotion"),
            selectAll: t("entertainment.selectAll"),
          }}
          isDynamic={true}
        />
      </Hero>

      <div>
        <TitleSection
          title={
            sectionCategoryLabel ?? t("promotions.content.header")
          }
          text={
            sectionDescription
              ? sectionDescription
              : t("promotions.content.description")
          }
        />
      </div>

      <container-wide>
        <Row
          className="g-4 gx-3"
          xs={1}
          md={2}
          lg={3}
          id="promo-card-container"
        >
          {loading ? (
            <div className="d-flex justify-content-center align-items-center w-100">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">{t("loading")}</span>
              </Spinner>
            </div>
          ) : error ? (
            <div className="text-center text-danger">
              <p>
                {t("error.somethingWentWrong")}: {error}
              </p>
            </div>
          ) : visibleCards.length === 0 ? (
            <div className="text-center w-100">
              <p>{t("promotions.content.noPromotionsAvailable")}</p>
            </div>
          ) : (
            visibleCards.map((el) => {
              const buttonSettings = el.url
                ? {
                    href: el.url,
                    label: el.buttonLabel || t("button.read-more"),
                  }
                : null;

              return (
                <Col key={el.key}>
                  <PgCard
                    id={el.key}
                    image={el.image}
                    imageAlt={el.imageAlt || el.title}
                    title={el.title}
                    subtitle={t(`promotions.categories.${el.category}`, {
                      defaultValue: el.category,
                    })}
                    text={el.content}
                    buttonSettings={buttonSettings}
                  />
                </Col>
              );
            })
          )}
        </Row>
      </container-wide>
    </PromoPage>
  );
};

const PromoPage = styled.div`
  .hero_text_container {
    p {
      margin-bottom: 1rem;
    }
  }
  .pg-card {
    .card-header {
      flex-grow: 0;
    }
    .card-body {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      .card-text {
        flex-grow: 1;
      }
    }
  }
`;

export default Promotions;
